import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { BackstoryPageProps } from './BackstoryTab';
import { useAuth } from 'hooks/AuthContext';
import { useAppState } from 'hooks/GlobalContext';
import * as Types from 'types/types';
import './VectorVisualizer.css';
import {
  Metadata,
  Node,
  PlotData,
  SendQueryDeps,
  buildPlotData,
  colorMap,
  emojiMap,
  emptyQuerySet,
  sendQuery,
} from './vector-visualizer/vectorData';
import { renderLoadingState } from './vector-visualizer/VectorNodePanels';
import { VectorVisualizerBody } from './vector-visualizer/VectorVisualizerBody';

interface VectorVisualizerProps extends BackstoryPageProps {
  inline?: boolean;
  rag?: Types.ChromaDBGetResponse;
  query?: string;
  filenameFilter?: string[];
  setQuery?: (query: string) => void;
  onQueryResult?: (query: string, filenames: string[]) => void;
}

const VectorVisualizer: React.FC<VectorVisualizerProps> = (props: VectorVisualizerProps) => {
  const { user, apiClient } = useAuth();
  const { rag, inline, sx, onQueryResult, setQuery, query, filenameFilter = [] } = props;
  const { setSnack } = useAppState();
  const isControlled = query !== undefined;
  const [plotData, setPlotData] = useState<PlotData[] | null>(null);
  const [internalQuery, setInternalQuery] = useState('');
  const inputValue = isControlled ? query : internalQuery;
  const [querySet, setQuerySet] = useState<Types.ChromaDBGetResponse>(rag || emptyQuerySet);
  const [result, setResult] = useState<Types.ChromaDBGetResponse | null>(null);
  const [vectorStatus, setVectorStatus] = useState('');
  const [vectorStatusType, setVectorStatusType] = useState<Types.ApiActivityType | null>(null);
  const [view2D, setView2D] = useState(true);
  const plotlyRef = useRef(null);
  const boxRef = useRef<HTMLElement>(null);
  const [node, setNode] = useState<Node | null>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [plotDimensions, setPlotDimensions] = useState({ width: 0, height: 0 });
  const navigate = useNavigate();
  const candidate = user?.userType === 'candidate' ? (user as Types.Candidate) : null;

  const queryDeps = useMemo<SendQueryDeps>(
    () => ({ apiClient, setQuerySet, setSnack, onQueryResult, isControlled, setInternalQuery }),
    [apiClient, isControlled, onQueryResult, setSnack]
  );

  useEffect(() => {
    if (!isControlled) return;
    if (query === internalQuery) return;
    setInternalQuery(query);
    if (query) {
      sendQuery(query, queryDeps);
    } else {
      setQuerySet(rag || emptyQuerySet);
    }
  }, [query, isControlled, internalQuery, queryDeps, rag]);

  useEffect(() => {
    if (!boxRef.current) return;
    requestAnimationFrame(() => {
      const plotContainer = document.querySelector('.plot-container') as HTMLElement;
      const svgContainer = document?.querySelector('.svg-container') as HTMLElement;
      if (!plotContainer || !svgContainer) return;
      const rect = plotContainer.getBoundingClientRect();
      svgContainer.style.width = `${rect.width}px`;
      svgContainer.style.height = `${rect.height}px`;
      if (plotDimensions.width !== rect.width || plotDimensions.height !== rect.height) {
        setPlotDimensions({ width: rect.width, height: rect.height });
      }
    });
  });

  useEffect(() => {
    if (result || !candidate) return;
    setVectorStatus('');
    setVectorStatusType(null);
    const controller = apiClient.getCandidateVectorsStream(view2D ? 2 : 3, {
      onStatus: status => {
        setVectorStatusType(status.activity);
        setVectorStatus(status.content);
      },
      onMessage: message => setResult(message.content[0]),
      onError: error => {
        console.error('Error obtaining collection information:', error.content);
        setSnack('Unable to obtain collection information.', 'error');
      },
      onComplete: () => {
        setVectorStatus('');
        setVectorStatusType(null);
      },
    });
    return () => controller.cancel();
  }, [result, setSnack, view2D, apiClient, candidate]);

  useEffect(() => {
    if (!result) return;
    const data = buildPlotData(result, view2D, querySet, filenameFilter);
    if (data) setPlotData(data);
  }, [result, querySet, view2D, filenameFilter]);

  const fetchRAGMeta = async (nextNode: Node): Promise<void> => {
    try {
      const response = await apiClient.getCandidateRAGContent(nextNode.id);
      setNode({ ...nextNode, fullContent: response.content });
    } catch (error) {
      const msg = `Error obtaining content for ${nextNode.id}.`;
      console.error(msg, error);
      setSnack(msg, 'error');
    }
  };

  const onNodeSelected = (metadata: Metadata): void => {
    if (metadata.docType === 'query') {
      setNode({
        ...metadata,
        content: `Similarity results for the query **${
          querySet.query || ''
        }**\n\nThe scatter graph shows the query in N-dimensional space, mapped to ${
          view2D ? '2' : '3'
        }-dimensional space. Larger dots represent relative similarity in N-dimensional space.\n`,
        emoji: emojiMap[String(metadata.docType)],
        sx: {
          m: 0.5,
          p: 2,
          width: '3rem',
          display: 'flex',
          alignContent: 'center',
          justifyContent: 'center',
          flexGrow: 0,
          flexWrap: 'wrap',
          backgroundColor: colorMap[String(metadata.docType)] || '#ff8080',
        },
      } as Node);
      return;
    }

    const nextNode = {
      content: 'Loading...',
      ...metadata,
      emoji: emojiMap[String(metadata.docType)] || '❓',
    } as Node;
    setNode(nextNode);
    fetchRAGMeta(nextNode);
  };

  if (!result) return renderLoadingState(vectorStatus, vectorStatusType);
  if (!candidate)
    return (
      <MissingCandidate
        onSelect={(): void => {
          void navigate('/find-a-candidate');
        }}
      />
    );

  const handleInputChange = (newValue: string): void => {
    if (isControlled) setQuery?.(newValue);
    else setInternalQuery(newValue);
  };

  return (
    <VectorVisualizerBody
      boxRef={boxRef}
      sx={sx}
      view2D={view2D}
      setView2D={setView2D}
      setResult={setResult}
      plotlyRef={plotlyRef}
      onNodeSelected={onNodeSelected}
      plotData={plotData}
      plotDimensions={plotDimensions}
      isMobile={isMobile}
      node={node}
      isControlled={isControlled}
      inline={inline}
      querySet={querySet}
      inputValue={inputValue}
      queryDeps={queryDeps}
      handleInputChange={handleInputChange}
    />
  );
};

function MissingCandidate({ onSelect }: { onSelect: () => void }): React.ReactElement {
  return (
    <Box sx={{ display: 'flex', flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}>
      <div>
        No candidate selected. Please <Button onClick={onSelect}>select a candidate</Button> first.
      </div>
    </Box>
  );
}

export type { VectorVisualizerProps };
export { VectorVisualizer };
